/**
 * EasyPaisaCheckout — modal shown on the Pricing page for Pakistani users.
 *
 * Steps:
 *   1. Fetch /api/manual-payments/info?country_code=PK   (account + PKR prices)
 *   2. Show the EasyPaisa account number + instructions
 *   3. Candidate uploads a screenshot of their payment + enters the reference (TID)
 *   4. POST multipart/form-data → /api/manual-payments/submit
 *   5. Show success screen — admin verifies within 2 hours
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, Copy, Upload, Check, Loader2, Smartphone, FileText, AlertCircle, ChevronRight,
} from "lucide-react";

export default function EasyPaisaCheckout({ open, planId, planName, onClose, onSubmitted }) {
  const [info, setInfo] = useState(null);
  const [phase, setPhase] = useState("instructions"); // instructions | upload | submitting | done
  const [reference, setReference] = useState("");
  const [senderAccount, setSenderAccount] = useState("");
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Hydrate from /me + pricing info on open
    (async () => {
      try {
        const [meRes, infoRes] = await Promise.all([
          api.get("/auth/me").catch(() => null),
          api.get("/manual-payments/info", { params: { country_code: "PK" } }),
        ]);
        setInfo(infoRes.data);
        const me = meRes?.data;
        if (me) {
          if (me.name) setFullName(me.name);
          if (me.email) setEmail(me.email);
          if (me.phone) setPhone(me.phone);
        }
      } catch (e) {
        setErr("Could not load payment details — please refresh and try again.");
      }
    })();
    // reset
    setPhase("instructions"); setReference(""); setSenderAccount("");
    setFile(null); setPreview(null); setErr(""); setNote("");
  }, [open]);

  if (!open) return null;
  const plan = info?.plans?.[planId];

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied to clipboard"); }
    catch (_e) { toast.error("Could not copy"); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { setErr("File too large (max 4 MB)"); return; }
    setErr("");
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const submit = async () => {
    setErr("");
    if (!fullName.trim()) { setErr("Full name is required"); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErr("Valid email is required"); return; }
    if (!phone.trim()) { setErr("Phone number is required"); return; }
    if (!reference.trim() || reference.trim().length < 4) { setErr("Reference / Transaction ID is required (min 4 chars)"); return; }
    if (!file) { setErr("Please attach the payment screenshot"); return; }
    setPhase("submitting");
    try {
      const fd = new FormData();
      fd.append("plan_id", planId);
      fd.append("full_name", fullName.trim());
      fd.append("email", email.trim().toLowerCase());
      fd.append("phone", phone.trim());
      fd.append("country_code", "PK");
      fd.append("reference", reference.trim());
      if (senderAccount.trim()) fd.append("sender_account", senderAccount.trim());
      if (note.trim()) fd.append("note", note.trim());
      fd.append("file", file);
      const res = await api.post("/manual-payments/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhase("done");
      onSubmitted?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Submission failed";
      setErr(msg);
      setPhase("upload");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 overflow-y-auto"
         style={{ background: "rgba(11,12,10,0.92)", backdropFilter: "blur(10px)" }}
         data-testid="easypaisa-modal">
      <div className="relative w-full max-w-2xl rounded-md border-2 my-auto"
           style={{ background: "#0F1110", borderColor: "rgba(247,127,0,0.5)",
                    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 30px rgba(247,127,0,0.4)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "rgba(247,127,0,0.25)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
                 style={{ background: "#F77F00", color: "#000" }}>
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: "#F77F00" }}>
                // EASYPAISA · PAKISTAN
              </div>
              <div className="font-black text-lg" style={{ fontFamily: "'Rajdhani', sans-serif", color: "#FCFAF3" }}>
                {planName || planId}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="easypaisa-close"
                  className="p-1.5 rounded-sm hover:bg-white/10" style={{ color: "#FCFAF3" }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {phase === "instructions" && (
            <div data-testid="easypaisa-instructions">
              {plan && (
                <div className="rounded-sm p-4 mb-5 flex items-center justify-between"
                     style={{ background: "rgba(247,127,0,0.08)", border: "1px solid rgba(247,127,0,0.4)" }}>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-70" style={{ color: "#FCFAF3" }}>AMOUNT DUE</div>
                    <div className="font-black text-3xl mt-1" style={{ fontFamily: "'Rajdhani', sans-serif", color: "#F77F00" }} data-testid="easypaisa-amount">
                      {plan.label}
                    </div>
                  </div>
                  <Smartphone className="w-12 h-12" style={{ color: "#F77F00" }} />
                </div>
              )}

              <div className="rounded-sm p-4 mb-4" style={{ background: "rgba(247,127,0,0.04)", border: "1px solid rgba(247,127,0,0.2)" }}>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-70 mb-1" style={{ color: "#FCFAF3" }}>SEND PAYMENT TO</div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-black text-2xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: "#FCFAF3" }} data-testid="easypaisa-account-number">
                      {info?.account_number || "Loading…"}
                    </div>
                    <div className="font-mono text-xs mt-1 opacity-70" style={{ color: "#FCFAF3" }}>
                      {info?.account_name || ""}
                    </div>
                  </div>
                  <button onClick={() => copy(info?.account_number || "")} data-testid="copy-account-number"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-sm font-mono text-xs tracking-[0.2em] uppercase"
                          style={{ background: "rgba(247,127,0,0.15)", border: "1px solid rgba(247,127,0,0.4)", color: "#F77F00" }}>
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
              </div>

              <ol className="space-y-2 mb-5">
                {[
                  "Open the EasyPaisa app on your phone (or visit any EasyPaisa retailer).",
                  "Send the exact amount shown above to the account number.",
                  "Take a clear screenshot of the payment confirmation receipt.",
                  "Copy the transaction reference (TID) shown in your receipt.",
                  "Click Continue, attach the screenshot and submit — we verify within 2 working hours.",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="font-black text-sm w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                         style={{ background: "#F77F00", color: "#000" }}>{i + 1}</div>
                    <div className="font-mono text-sm leading-6" style={{ color: "#FCFAF3" }}>{step}</div>
                  </li>
                ))}
              </ol>

              <button onClick={() => setPhase("upload")} data-testid="easypaisa-continue"
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 font-mono text-sm tracking-[0.25em] uppercase rounded-sm transition-all hover:-translate-y-0.5"
                      style={{ background: "#F77F00", color: "#000", boxShadow: "0 0 18px rgba(247,127,0,0.65)" }}>
                I have paid — continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {(phase === "upload" || phase === "submitting") && (
            <div data-testid="easypaisa-upload-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <Field label="Full name" value={fullName} onChange={setFullName} testid="ep-fullname" required />
                <Field label="Email" value={email} onChange={setEmail} testid="ep-email" type="email" required />
                <Field label="Phone (used to pay)" value={phone} onChange={setPhone} testid="ep-phone" required />
                <Field label="Sender account (optional)" value={senderAccount} onChange={setSenderAccount} testid="ep-sender" placeholder="e.g. 0300 1234567" />
                <Field label="Transaction ID / Reference" value={reference} onChange={setReference} testid="ep-reference" required
                       placeholder="From your receipt" />
                <Field label="Note (optional)" value={note} onChange={setNote} testid="ep-note" placeholder="Any extra info for our team" />
              </div>

              <div className="rounded-sm p-4 mb-3"
                   style={{ background: "rgba(247,127,0,0.04)", border: "1.5px dashed rgba(247,127,0,0.5)" }}>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" hidden onChange={onPickFile}
                       data-testid="ep-file-input" />
                {!file ? (
                  <button onClick={() => fileInputRef.current?.click()} data-testid="ep-upload-trigger"
                          className="w-full flex flex-col items-center justify-center gap-2 py-6 font-mono text-sm tracking-[0.2em] uppercase rounded-sm"
                          style={{ color: "#F77F00" }}>
                    <Upload className="w-8 h-8" />
                    Attach payment screenshot
                    <span className="text-xs opacity-60 normal-case tracking-normal">PNG, JPG, WebP or PDF · max 4 MB</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-3" data-testid="ep-file-preview">
                    {preview ? (
                      <img src={preview} alt="Payment receipt"
                           className="w-20 h-20 object-cover rounded-sm" style={{ border: "1px solid rgba(247,127,0,0.4)" }} />
                    ) : (
                      <div className="w-20 h-20 rounded-sm flex items-center justify-center"
                           style={{ background: "rgba(247,127,0,0.08)", border: "1px solid rgba(247,127,0,0.4)" }}>
                        <FileText className="w-8 h-8" style={{ color: "#F77F00" }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm truncate" style={{ color: "#FCFAF3" }}>{file.name}</div>
                      <div className="font-mono text-xs opacity-60" style={{ color: "#FCFAF3" }}>
                        {(file.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button onClick={() => { setFile(null); setPreview(null); }} data-testid="ep-file-remove"
                            className="p-2 rounded-sm" style={{ color: "#FCFAF3" }}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {err && (
                <div className="rounded-sm p-3 mb-3 flex items-start gap-2"
                     style={{ background: "rgba(214,40,40,0.08)", border: "1px solid #D62828", color: "#D62828" }}
                     data-testid="ep-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="font-mono text-sm">{err}</div>
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => setPhase("instructions")} data-testid="ep-back"
                        className="px-4 py-3 font-mono text-xs tracking-[0.25em] uppercase rounded-sm"
                        style={{ background: "transparent", border: "1px solid rgba(247,127,0,0.3)", color: "#FCFAF3" }}>
                  Back
                </button>
                <button onClick={submit} disabled={phase === "submitting"} data-testid="ep-submit"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 font-mono text-sm tracking-[0.25em] uppercase rounded-sm disabled:opacity-60"
                        style={{ background: "#F77F00", color: "#000", boxShadow: "0 0 18px rgba(247,127,0,0.65)" }}>
                  {phase === "submitting" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Submit for verification</>
                  )}
                </button>
              </div>
            </div>
          )}

          {phase === "done" && (
            <div className="text-center py-6" data-testid="easypaisa-done">
              <div className="w-16 h-16 rounded-full inline-flex items-center justify-center mb-4"
                   style={{ background: "#16A34A", color: "#fff", boxShadow: "0 0 24px rgba(22,163,74,0.5)" }}>
                <Check className="w-9 h-9" />
              </div>
              <div className="font-black text-2xl mb-2" style={{ fontFamily: "'Rajdhani', sans-serif", color: "#FCFAF3" }}>
                Payment received — under review
              </div>
              <div className="font-mono text-sm leading-7 max-w-md mx-auto" style={{ color: "rgba(252,250,243,0.7)" }}>
                Our finance team is verifying your transaction. You will get an email and your plan will activate
                automatically — usually within <b>2 working hours</b>.
              </div>
              <button onClick={onClose} data-testid="ep-close-done"
                      className="mt-6 inline-flex items-center gap-2 px-5 py-3 font-mono text-xs tracking-[0.25em] uppercase rounded-sm"
                      style={{ background: "rgba(247,127,0,0.15)", border: "1px solid rgba(247,127,0,0.4)", color: "#F77F00" }}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, required, type = "text", placeholder }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-70 mb-1.5" style={{ color: "#FCFAF3" }}>
        {label} {required && <span style={{ color: "#F77F00" }}>*</span>}
      </div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""}
             data-testid={testid}
             className="w-full px-3 py-2.5 rounded-sm font-mono text-sm outline-none"
             style={{ background: "rgba(247,127,0,0.04)", border: "1px solid rgba(247,127,0,0.3)", color: "#FCFAF3" }} />
    </label>
  );
}
